import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { displayToast } from "../../../components/app/AppToast";
import {
  clearPostLoginRedirectPath,
  memorizePostLoginRedirectPath,
} from "../../app/utils/postLoginRedirect";
import { useFirebaseUser } from "./useFirebaseUser";
import { isDesktopBrowserLayout } from "../../../common/hooks/useIsDesktopView";

type HookParams = {
  memorizePath?: boolean;
  skipIf?: boolean;
};

export function useCheckUserAccessRegistered(params?: HookParams) {
  const history = useHistory();
  const { t } = useTranslation();
  const { firebaseUser, isLoadingFirebaseUser } = useFirebaseUser();
  const skip = params?.skipIf === true;

  useEffect(() => {
    if (skip) return;

    // The memorized path is cleared only where this hook memorizes a new one.
    // A desktop login from the gate over this page reads the path left by the
    // page that sent the reader here, and the session opens before that read.
    if (!isLoadingFirebaseUser && !firebaseUser) {
      // Desktop already has a wall for this, and it is a better one: the auth
      // gate modal, mounted by the router over whichever private route was
      // asked for, with the app visible behind it. Redirecting to `/login`
      // from inside the page would yank the reader off that page and print a
      // "please login" toast behind a modal that is already saying it.
      //
      // Every consumer of this hook is a private surface, so on desktop the
      // gate is the thing standing in front of them. On mobile this redirect
      // is still the wall.
      if (isDesktopBrowserLayout()) return;
      displayToast(t("✋ Please login before using this feature"), "warning");
      clearPostLoginRedirectPath();
      if (params?.memorizePath) memorizePostLoginRedirectPath();
      history.replace("/login" + globalThis.location.search);
    }
  }, [firebaseUser, isLoadingFirebaseUser, skip]);
}
